import { CircularProgress, Tooltip } from "@mui/material";
import CockpitApi from "api/CockpitApi";
import { formatToBRL, formatToCPFOrCNPJ } from "@utils/formatters";
import Modal from "components/modal/modal";
import PageFrame from "components/page-frame/pageFrame";
import PDFViewer from "@components/pdf/pdfViewer";
import HistoryDetails from "@components/historyDetails/historyDetails";
import { useSelectedRootCompany } from "@contexts/app/context";
import { useAuth } from "@contexts/auth";
import PermissionControl from "@components/permission/PermissionControl";
import { PERMISSIONS } from "@utils/permissions";
import { buildListingQuery } from "@utils/listingQuery";
import { Link } from "react-router-dom";
import { CockpitDocument, CockpitDocumentType } from "model/cockpit/CockpitDocument";
import { nfeFilterStatus } from "model/NFeFilterStatus";
import { nfceFilterStatus } from "model/nfce/NFCeFilterStatus";
import { cteFilterStatus } from "model/cte/CTeFilterStatus";
import { mdfeFilterStatus } from "model/mdfe/MDFeFilterStatus";
import { mdfeStatus } from "model/mdfe/MDFeStatus";
import { MDFeHistory } from "model/mdfe/MDFeHistory";
import fileDownload from "js-file-download";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

const StyledFrame = styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;

    display: flex;
    flex-direction: column;

    .centered {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .search-panel {
        background-color: var(--white);
        border: 1px solid var(--grey-light);
        border-radius: 8px;
        padding: 16px 20px;

        .search-label {
            font-weight: bold;
            color: var(--color-darkest);
        }

        .search-hint {
            color: var(--grey-medium-light);
            font-size: .8rem;
        }

        .search-row {
            display: flex;
            margin-top: 12px;

            > button {
                margin-left: 10px;
                height: 36px;
            }
        }

        .search-input {
            flex: 1;
            height: 36px;
            display: flex;
            align-items: center;

            border: 1px solid var(--grey-light);
            border-radius: 8px;
            background-color: var(--white);

            :focus-within {
                border-color: var(--color-primary);
            }

            &.invalid {
                border-color: var(--color-danger);
            }

            > i {
                padding: 0 10px;
                color: var(--grey-regular);
            }

            > input {
                flex: 1;
                height: 100%;
                border: none;
                outline: none;
                background: transparent;
                font-family: 'JetBrains Regular';
                font-size: .85rem;
            }
        }

        .search-error {
            color: var(--color-danger);
            font-size: .8rem;
            margin-top: 6px;
        }
    }

    .summary {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin-top: 18px;

        > span {
            margin-right: 18px;
        }

        .pill {
            border: 1px solid var(--grey-light);
            border-radius: 15px;
            background-color: var(--white);
            padding: 0 10px;
            margin-right: 8px;
            font-family: 'JetBrains Regular';
            font-size: .78rem;
        }
    }

    .key-info {
        display: flex;
        align-items: flex-start;
        margin-top: 12px;
        padding: 6px 10px;
        border-radius: 8px;
        font-size: .8rem;
        background-color: #eef8ff;
        border: 1px solid #bfe5ff;
        color: #1f6d99;

        > i {
            margin-right: 8px;
        }
    }

    .cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(440px, 1fr));
        gap: 16px;
        margin-top: 12px;
        padding-bottom: 16px;
    }

    .card {
        display: flex;
        flex-direction: column;
        overflow: hidden;

        background-color: var(--white);
        border: 1px solid var(--grey-light);
        border-radius: 8px;

        /* busca por chave: o dono da chave e os que a referenciam */
        &.key-match {
            border-color: var(--color-primary);
            box-shadow: 0 0 0 1px var(--color-primary);
        }

        .key-badge {
            margin-left: 8px;
            padding: 0 8px;
            vertical-align: middle;
            border: 1px solid var(--color-primary);
            border-radius: 15px;
            background-color: var(--grey-lightest);
            color: var(--color-dark);
            font-family: 'TitilliumWeb';
            font-size: .65rem;
            font-weight: bold;
            text-transform: uppercase;
        }

        .card-accent {
            height: 4px;
        }

        .card-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 12px 16px 8px;

            .doc-type {
                font-family: 'JetBrains Semi Bold';
                font-size: 1.15rem;
                color: var(--color-darkest);
            }

            .access-key {
                font-family: 'JetBrains Regular';
                font-size: .74rem;
                color: var(--grey-medium-light);
                white-space: nowrap;

                > i {
                    margin-left: 6px;
                    cursor: pointer;
                    color: var(--grey-regular);

                    :hover {
                        color: var(--color-primary);
                    }
                }
            }

            .status {
                display: flex;
                align-items: center;
                flex-shrink: 0;
                margin-left: 12px;
                font-weight: bold;

                > div {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    margin-right: 6px;
                }
            }
        }

        .card-main {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding: 0 16px 10px;
            border-bottom: 1px solid var(--grey-lightest);

            .value {
                font-size: 1.3rem;
                font-weight: bold;
                color: var(--grey-darkest);

                > small {
                    font-size: .85rem;
                    color: var(--grey-medium-light);
                }
            }

            .amount {
                text-align: right;

                .value {
                    color: var(--color-darkest);
                }
            }
        }

        .label {
            font-size: .72rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: .3px;
            color: var(--grey-regular);
        }

        .card-body {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px 16px;
            padding: 10px 16px;

            .sub-label {
                font-size: .75rem;
                color: var(--grey-medium-light);
            }
        }

        .alert {
            display: flex;
            margin: 0 16px 10px;
            padding: 6px 10px;
            border-radius: 8px;
            font-size: .8rem;

            > i {
                margin-right: 8px;
            }

            &.danger {
                background-color: #fff0f6;
                border: 1px solid #ffc2da;
                color: #b00d52;
            }

            &.info {
                background-color: #eef8ff;
                border: 1px solid #bfe5ff;
                color: #1f6d99;
            }

            &.warn {
                background-color: #fffbe6;
                border: 1px solid #f5e08a;
                color: #7a6000;
            }

            &.neutral {
                background-color: #f4f4f4;
                border: 1px solid #e2e2e2;
                color: var(--grey-medium);
            }
        }

        .card-actions {
            display: flex;
            margin-top: auto;
            padding: 10px 16px 12px;
            border-top: 1px solid var(--grey-lightest);

            > button {
                width: fit-content;
                margin-right: 8px;
            }

            .listing-link {
                margin-left: auto;
                align-self: center;
                white-space: nowrap;
                font-size: .8rem;
                font-weight: bold;
                text-decoration: none;
                color: var(--color-dark);

                :hover {
                    text-decoration: underline;
                }
            }
        }
    }

    .empty {
        margin-top: 40px;
        text-align: center;
        color: var(--grey-medium-light);

        > i {
            font-size: 2.6rem;
            color: var(--grey-light);
        }

        > h4 {
            margin: 8px 0 4px;
            color: var(--grey-darkest);
        }
    }

    .hist-item-frame {
        height: fit-content;
        display: flex;
        align-items: top;

        .status-indicator {
            height: 6px;
            width: 6px;
            border-radius: 50%;
            margin-top: 6px;
            margin-left: 8px;
            margin-right: 8px;
            margin-bottom: 6px;
            border: 3px solid var(--color-success);
        }

        .status-link-bar {
            width: 3px;
            min-height: 100%;
            background-color: var(--color-success);
            border-radius: 6px;
            margin-top: 7px;
            margin-left: 12px;
            margin-right: 12px;
        }
    }

    .div-table-cell {
        display: table-cell;
        vertical-align: top;
        padding-left: 10px;
        padding-bottom: 10px;
        padding-top: 15px;
    }

    .line-separator {
        display: table-cell;
        border-left: none;
        border-top: var(--grey-light);
    }
`;

const ModalFrame = styled.div`
        top: 6px;
        left: -14px;
        width: 100%;
        position: absolute;
        transform: translateY(-96px);
        z-index: 1000;
`;

/* chave de acesso: 44 posições, só o CNPJ (posições 7 a 18) pode ter letras (CNPJ alfanumérico) */
const ACCESS_KEY_REGEX = /^\d{6}[0-9A-Z]{12}\d{26}$/;

/* DV da chave por módulo 11; com o CNPJ alfanumérico cada caractere vale o código ASCII - 48 ('A' = 17 ... 'Z' = 42) */
const accessKeyDv = (key43: string) => {
    let weight = 2, sum = 0;
    for (let i = key43.length - 1; i >= 0; i--) {
        sum += (key43.charCodeAt(i) - 48) * weight;
        weight = weight === 9 ? 2 : weight + 1;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
}

type SearchInput =
    | { kind: 'key', value: string }
    | { kind: 'trx', value: string }
    | { kind: 'error', message: string };

/* o mesmo campo aceita chave ou transactionId: o que tem formato de chave é chave; o resto é transactionId */
const classifySearch = (raw: string): SearchInput => {
    const value = raw.trim();
    const key = value.replace(/[\s.\-/]/g, '').toUpperCase();

    if (ACCESS_KEY_REGEX.test(key)) {
        if (key.charCodeAt(43) - 48 !== accessKeyDv(key.substring(0, 43))) {
            return { kind: 'error', message: 'Dígito verificador da chave de acesso inválido.' };
        }
        return { kind: 'key', value: key };
    }
    /* só números e quase 44: provavelmente uma chave digitada pela metade */
    if (/^\d{40,48}$/.test(key)) {
        return { kind: 'error', message: `A chave de acesso deve ter 44 caracteres (${key.length} informados).` };
    }
    return { kind: 'trx', value };
}

const TYPE_LABEL: Record<CockpitDocumentType, string> = { NFE: 'NFe', NFCE: 'NFCe', CTE: 'CTe', MDFE: 'MDFe' };

/* selo do card na busca por chave */
const KEY_MATCH_LABEL: Record<NonNullable<CockpitDocument['keyMatch']>, string> = {
    OWN: 'Chave pesquisada',
    REFERENCE: 'Referencia a chave pesquisada',
    REFERENCED: 'Referenciado pela chave pesquisada'
};

/* o botão sai em maiúsculas pelo .icon-button */
const PDF_LABEL: Record<CockpitDocumentType, string> = { NFE: 'DANFe', NFCE: 'DANFCe', CTE: 'DACTe', MDFE: 'DAMDFe' };

const PARTY_LABEL: Partial<Record<CockpitDocumentType, string>> = { NFE: 'Destinatário', NFCE: 'Consumidor', CTE: 'Tomador' };

const FILTER_STATUS: Record<CockpitDocumentType, { label: string, value: string | null, refColor: string }[]> = {
    NFE: nfeFilterStatus,
    NFCE: nfceFilterStatus,
    CTE: cteFilterStatus,
    MDFE: mdfeFilterStatus
};

/* "Abrir na listagem": a rota e a permissão que o App.tsx exige para ela (sem a permissão o link não aparece) */
const LISTING: Record<CockpitDocumentType, { path: string, permission: string }> = {
    NFE: { path: 'nfe/notas', permission: PERMISSIONS.DOOTAX.NFE.GERENCIAR.VISUALIZAR },
    NFCE: { path: 'nfce/notas', permission: PERMISSIONS.DOOTAX.NFCE.GERENCIAR.VISUALIZAR },
    CTE: { path: 'cte/notas', permission: PERMISSIONS.DOOTAX.CTE.GERENCIAR.VISUALIZAR },
    MDFE: { path: 'mdfe/notas', permission: PERMISSIONS.DOOTAX.MDFE.GERENCIAR.VISUALIZAR }
};

/* os mesmos status em que cada listagem libera "Baixar arquivos" (disableAction de cada listPage) */
const PDF_STATUSES: Record<CockpitDocumentType, string[]> = {
    NFE: ['AUTORIZADO', 'CANCELADO'],
    NFCE: ['AUTORIZADO', 'CANCELADO', 'NFCE_OFFLINE', 'ERRO_OFFLINE'],
    CTE: ['AUTORIZADO', 'CANCELADO'],
    MDFE: ['AUTORIZADO', 'CANCELADO', 'ENCERRADO', 'MDFE_OFFLINE', 'ERRO_OFFLINE']
};

/* como no MDFeListPage: o XML de autorização (mdfeProc) só existe depois da transmissão da contingência */
const MDFE_OFFLINE_STATUSES: string[] = ['MDFE_OFFLINE', 'ERRO_OFFLINE'];

const hasPdf = (doc: CockpitDocument) => PDF_STATUSES[doc.type].includes(doc.status || '');
const hasXml = (doc: CockpitDocument) => hasPdf(doc) && !(doc.type === 'MDFE' && MDFE_OFFLINE_STATUSES.includes(doc.status || ''));

/* o grupo do *AuthStatusDTO, com a cor da listagem; o MDFe offline mostra o status dele, como no MDFeListPage */
const getStatus = (doc: CockpitDocument) => {
    const group = FILTER_STATUS[doc.type].find(s => s.value === doc.reportStatus);
    const label = doc.type === 'MDFE' && MDFE_OFFLINE_STATUSES.includes(doc.status || '')
        ? mdfeStatus.find(st => st.value === doc.status)?.label
        : group?.label;
    return { label: label || doc.status, color: group?.refColor || 'var(--grey-regular)' };
}

/* modal do CTe tem dois dígitos e o do MDFe um (CTeService.getModalDescription e MDFeListPage) */
const getModalLabel = (doc: CockpitDocument) => {
    const modal = doc.type === 'CTE' ? (doc.modal || '').replace(/^0/, '') : doc.modal;
    switch (modal) {
        case '1': return 'Rodoviário';
        case '2': return 'Aéreo';
        case '3': return 'Aquaviário';
        case '4': return 'Ferroviário';
        case '5': return 'Dutoviário';
        case '6': return 'Multimodal';
        default: return doc.modal;
    }
}

/* o backend manda dd/MM/yyyy HH:mm:ss; o card mostra até os minutos */
const toMinutes = (date?: string) => (date || '').substring(0, 16);

const formatAccessKey = (key?: string) => (key || '').replace(/(.{4})(?=.)/g, '$1 ');

const formatDocNum = (num?: number) => String(num ?? 0).padStart(9, '0').replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');

const CockpitDocumentsPage = () => {

    const api = new CockpitApi();
    const selectedRootCompany = useSelectedRootCompany();
    const { tenantInfo } = useAuth();

    const [query, setQuery] = useState('');
    const [validationError, setValidationError] = useState<string>();
    const [searching, setSearching] = useState(false);
    const [documents, setDocuments] = useState<CockpitDocument[]>();
    /* o tipo da busca que gerou o resultado na tela (o campo pode já ter sido alterado) */
    const [searchedBy, setSearchedBy] = useState<'key' | 'trx'>('trx');

    const [activeDoc, setActiveDoc] = useState<CockpitDocument>();
    const [history, setHistory] = useState<MDFeHistory[]>([]);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [pdf, setPdf] = useState<any>();
    const [pdfFilename, setPdfFilename] = useState<any>();
    const [downloadingXmlId, setDownloadingXmlId] = useState<number>();

    /* o resultado é da empresa selecionada: ao trocar de empresa, a consulta precisa ser refeita */
    useEffect(() => {
        setDocuments(undefined);
    }, [selectedRootCompany?.id]);

    const handleSearch = () => {
        setValidationError(undefined);

        if (!query.trim()) {
            setValidationError('Informe a chave de acesso ou o ID da transação.');
            return;
        }

        const search = classifySearch(query);
        if (search.kind === 'error') {
            setValidationError(search.message);
            return;
        }

        setSearching(true);
        api.fetchDocuments(search.kind === 'key' ? { accessKey: search.value } : { transactionId: search.value }, selectedRootCompany?.id || null)
            .then(docs => {
                setSearchedBy(search.kind);
                setDocuments(docs);
            })
            .catch(() => setDocuments(undefined))
            .finally(() => setSearching(false));
    }

    const handleCopyAccessKey = (key?: string) => {
        if (!key) {
            return;
        }
        /* o clipboard só existe em contexto seguro (HTTPS/localhost) */
        if (!navigator.clipboard) {
            toast.error('Não foi possível copiar a chave de acesso.');
            return;
        }
        navigator.clipboard.writeText(key)
            .then(() => toast.success('Chave de acesso copiada.'))
            .catch(() => toast.error('Não foi possível copiar a chave de acesso.'));
    }

    const handleShowHistory = (doc: CockpitDocument) => {
        setActiveDoc(doc);
        setHistory([]);
        setHistoryDialogOpen(true);
        setLoadingHistory(true);

        api.fetchHistory(doc)
            .then(res => setHistory(res.data))
            .finally(() => setLoadingHistory(false));
    }

    const handleDownloadXml = (doc: CockpitDocument) => {
        setDownloadingXmlId(doc.id);
        api.downloadXml(doc).finally(() => setDownloadingXmlId(undefined));
    }

    const handleShowPdf = (doc: CockpitDocument) => {
        setActiveDoc(doc);
        setPdf(undefined);
        setPdfFilename(undefined);
        setPdfDialogOpen(true);

        api.downloadPdf(doc).then(res => {
            if (res) {
                setPdf(res.data);
                setPdfFilename(res.headers.filename);
            }
        });
    }

    /* imprime por um iframe oculto, como nas listagens */
    const handlePrint = (pdfBytes: Blob) => {
        const pdfUrl = URL.createObjectURL(pdfBytes);
        const frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.src = pdfUrl;
        frame.onload = () => {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(frame);
                URL.revokeObjectURL(pdfUrl);
            }, 60000);
        };
        document.body.appendChild(frame);
    }

    const renderAlerts = (doc: CockpitDocument) => {
        const feminine = doc.type === 'NFE' || doc.type === 'NFCE';
        switch (doc.reportStatus) {
            case 'FALHA_EMISSAO':
                return <div className="alert danger">
                    <i className="bi bi-exclamation-octagon" />
                    <span>{doc.failureReason || 'Consulte o histórico para ver o motivo da falha.'}</span>
                </div>;
            case 'EM_PROCESSAMENTO':
                return <div className="alert info">
                    <i className="bi bi-hourglass-split" />
                    <span>Aguardando retorno da SEFAZ. Consulte novamente em instantes.</span>
                </div>;
            case 'CANCELADO':
                return <div className="alert warn">
                    <i className="bi bi-x-octagon" />
                    <span><b>{feminine ? 'Cancelada' : 'Cancelado'} em {toMinutes(doc.cancelDate)}</b></span>
                </div>;
            case 'ENCERRADO':
                return <div className="alert neutral">
                    <i className="bi bi-flag" />
                    <span><b>Encerrado em {toMinutes(doc.closureDate)}</b></span>
                </div>;
            default:
                return null;
        }
    }

    const renderCard = (doc: CockpitDocument) => {
        const status = getStatus(doc);
        const partyLabel = PARTY_LABEL[doc.type];

        return <article className={`card${doc.keyMatch ? ' key-match' : ''}`} key={`${doc.type}-${doc.id}`}>
            <div className="card-accent" style={{ backgroundColor: status.color }} />
            <div className="card-head">
                <div>
                    <div className="doc-type">
                        {`<${TYPE_LABEL[doc.type]}/>`}
                        {doc.keyMatch && <span className="key-badge">{KEY_MATCH_LABEL[doc.keyMatch]}</span>}
                    </div>
                    {doc.accessKey && <div className="access-key">
                        {formatAccessKey(doc.accessKey)}
                        <Tooltip title="Copiar chave de acesso" placement="top">
                            <i className="bi bi-files" onClick={() => handleCopyAccessKey(doc.accessKey)} />
                        </Tooltip>
                    </div>}
                </div>
                <div className="status" style={{ color: status.color }}>
                    <div style={{ backgroundColor: status.color }} />
                    <span>{status.label}</span>
                </div>
            </div>

            <div className="card-main">
                <div>
                    <div className="label">Número / Série</div>
                    <div className="value">{formatDocNum(doc.docNum)} <small>/ {(doc.series || '').padStart(3, '0')}</small></div>
                </div>
                <div className="amount">
                    <div className="label">{doc.type === 'MDFE' ? 'Valor da carga' : 'Valor total'}</div>
                    <div className="value">{formatToBRL(doc.amount || 0)}</div>
                </div>
            </div>

            <div className="card-body">
                <div>
                    <div className="label">Emissão</div>
                    <div>{toMinutes(doc.issueDate)}</div>
                    {doc.authDate && <div className="sub-label">Autorizado em {toMinutes(doc.authDate)}</div>}
                </div>
                {partyLabel && <div>
                    <div className="label">{partyLabel}</div>
                    <div>{doc.partyName || '-'}</div>
                    {doc.partyTaxId && <div className="sub-label">{formatToCPFOrCNPJ(doc.partyTaxId)}</div>}
                </div>}
                {(doc.type === 'CTE' || doc.type === 'MDFE') && <div>
                    <div className="label">Modal / Percurso</div>
                    <div>{getModalLabel(doc)} · {doc.startUf} → {doc.endUf}</div>
                </div>}
                {doc.type === 'MDFE' && <div>
                    <div className="label">Documentos transportados</div>
                    <div>{doc.nfeQuantity || 0} NFe · {doc.cteQuantity || 0} CTe</div>
                </div>}
            </div>

            {renderAlerts(doc)}

            <div className="card-actions">
                {hasXml(doc) && <button className="icon-button centered" disabled={downloadingXmlId === doc.id} onClick={() => handleDownloadXml(doc)}>
                    {downloadingXmlId === doc.id
                        ? <CircularProgress size={14} style={{ 'color': '#19ea81' }} />
                        : <><i className="bi bi-download" /><span>XML</span></>}
                </button>}
                {hasPdf(doc) && <button className="icon-button centered" onClick={() => handleShowPdf(doc)}>
                    <i className="bi bi-file-earmark-pdf" /><span>{PDF_LABEL[doc.type]}</span>
                </button>}
                <button className="icon-button centered" onClick={() => handleShowHistory(doc)}>
                    <i className="bi bi-clock-history" /><span>Histórico</span>
                </button>
                <PermissionControl requiredPermissions={[LISTING[doc.type].permission]}>
                    {/* nova aba: o resultado do cockpit continua aberto; sessão e empresa vêm do localStorage */}
                    <Link className="listing-link" target="_blank" rel="noopener noreferrer"
                        to={`/${tenantInfo?.tenantAlias}/${LISTING[doc.type].path}${buildListingQuery(doc.accessKey, doc.transactionId)}`}>
                        Abrir na listagem <i className="bi bi-box-arrow-up-right" />
                    </Link>
                </PermissionControl>
            </div>
        </article>;
    }

    const renderResult = () => {
        if (!documents) {
            return <div className="empty">
                <i className="bi bi-search" />
                <h4>Consulte um documento ou uma transação</h4>
                <div>Digite a chave de acesso ou o ID da transação para ver o status de todos os documentos vinculados.</div>
            </div>;
        }

        const scope = selectedRootCompany ? 'na empresa selecionada' : 'nas suas empresas';

        if (!documents.length) {
            return <div className="empty">
                <i className="bi bi-inboxes" />
                <h4>Nenhum documento encontrado</h4>
                {searchedBy === 'key'
                    ? <div>Não há documento com esta chave de acesso nem documento que a referencie {scope}.</div>
                    : <div>Não há documentos vinculados a este ID de transação {scope}.</div>}
                {selectedRootCompany && <div style={{ marginTop: '6px', fontSize: '.8rem' }}>Verifique se a empresa selecionada no topo é a correta.</div>}
                {/* o doc_dfe_referenciado só é preenchido nas emissões feitas depois dele */}
                {searchedBy === 'key' && <div style={{ marginTop: '6px', fontSize: '.8rem' }}>Documentos que referenciam a chave só são encontrados se emitidos a partir desta versão.</div>}
            </div>;
        }

        const countByType = documents.reduce((acc, doc) => ({ ...acc, [doc.type]: (acc[doc.type] || 0) + 1 }), {} as Record<string, number>);
        const keyIsOurs = documents.some(doc => doc.keyMatch === 'OWN');

        return <>
            <div className="summary">
                <span><b>{documents.length}</b> {documents.length === 1 ? 'documento vinculado' : 'documentos vinculados'} {searchedBy === 'key' ? 'à chave de acesso' : 'à transação'}</span>
                {Object.entries(countByType).map(([type, count]) =>
                    <span key={type} className="pill">{`${count}× <${TYPE_LABEL[type as CockpitDocumentType]}/>`}</span>)}
            </div>
            {searchedBy === 'key' && !keyIsOurs && <div className="key-info">
                <i className="bi bi-info-circle" />
                <span>A chave pesquisada não é de um documento emitido {selectedRootCompany ? 'por esta empresa' : 'pelas suas empresas'}. Exibindo os documentos que a referenciam.</span>
            </div>}
            <div className="cards">{documents.map(renderCard)}</div>
        </>;
    }

    // ----------------------------
    // RETURN
    // ----------------------------
    return <PageFrame><StyledFrame>
        <section className="search-panel">
            <div className="search-label">Chave de acesso ou ID da transação</div>
            <div className="search-hint">Informe a chave de 44 dígitos de um documento fiscal ou o transactionId enviado na integração. Exibimos todos os documentos vinculados.</div>
            <div className="search-row">
                <div className={`search-input ${validationError ? 'invalid' : ''}`}>
                    <i className="bi bi-search" />
                    <input value={query}
                        placeholder="Ex.: 3526 1011 2223 3300 0181 5500 ... ou RPR-2026-1014-0042"
                        spellCheck={false}
                        autoComplete="off"
                        onChange={(event) => { setQuery(event.target.value); setValidationError(undefined); }}
                        onKeyDown={(event) => event.key === 'Enter' && handleSearch()} />
                </div>
                <button className="icon-button action-button centered" style={{ width: '140px' }} disabled={searching} onClick={handleSearch}>
                    {searching
                        ? <CircularProgress size={14} style={{ 'color': '#FFFFFF' }} />
                        : <><i className="bi bi-search" /><span>Consultar</span></>}
                </button>
            </div>
            {validationError && <div className="search-error">{validationError}</div>}
        </section>

        {renderResult()}

        {/* DIALOG - VISUALIZAR PDF */}
        {pdfDialogOpen && !!activeDoc && <ModalFrame>
            <Modal
                headerTitle={`Visualizar ${PDF_LABEL[activeDoc.type].toUpperCase()}`}
                closeOnClickAway={true}
                handleClose={() => {
                    setPdfDialogOpen(false);
                    setPdf(undefined);
                    setPdfFilename(undefined);
                }}
                headerActions={[
                    {
                        icon: (<i title="Imprimir" className="bi bi-printer" style={{ fontSize: '1rem' }} />),
                        actionCallback: () => pdf && handlePrint(pdf)
                    },
                    {
                        icon: (<i title="Download" className="bi bi-download" style={{ fontSize: '1rem' }} />),
                        actionCallback: () => pdf && fileDownload(pdf, pdfFilename)
                    }
                ]}
                loading={false}>
                <PDFViewer pdf={pdf} />
            </Modal>
        </ModalFrame>}

        {/* DIALOG - HISTÓRICO (mesma tabela das listagens) */}
        {historyDialogOpen && !!activeDoc && <ModalFrame>
            <Modal
                headerTitle={`Histórico do ${TYPE_LABEL[activeDoc.type]} ${formatDocNum(activeDoc.docNum)}`}
                closeOnClickAway={false}
                handleClose={() => setHistoryDialogOpen(false)}
                loading={loadingHistory}
            >
                <div style={{ display: 'table-table', border: `1px solid ${'var(--grey-light)'}`, borderRadius: '10px' }}>
                    <div style={{ display: 'table-row', fontWeight: 'bold', textAlign: "center" }}>
                        <div style={{ width: '150px', display: 'table-cell', padding: '10px' }}>Data</div>
                        <div style={{ display: 'table-cell' }}>Status</div>
                        <div style={{ minWidth: '100px', display: 'table-cell' }}>Usuário</div>
                    </div>
                    <div style={{ display: 'table-row' }}>
                        <hr className="line-separator" />
                        <hr className="line-separator" />
                        <hr className="line-separator" />
                    </div>
                    {history.map((histItem, index) => <div key={index} style={{ width: '100%', display: 'table-row', padding: '5px' }} >
                        <div className="div-table-cell" style={{ width: '150px' }}>
                            {histItem.createdAt}
                        </div>
                        <div className="div-table-cell">
                            <div className="hist-item-frame">
                                <div>
                                    <Tooltip title={histItem.status} placement='top' >
                                        <div className="status-indicator" />
                                    </Tooltip>
                                    {index < history.length - 1 ? <p className="status-link-bar" ></p> : ''}
                                </div>
                                <div style={{ paddingLeft: '15px' }}>
                                    <span style={{
                                        color: `${(index === 0 && histItem.status) ? 'var(--color-success)' : ''}`,
                                        fontSize: '100%',
                                        fontWeight: `${(index === 0 && histItem.status) ? 'bold' : ''}`
                                    }}>{histItem.message}</span>
                                    {histItem.details && !!histItem.details.length && (<HistoryDetails items={histItem.details} />)}
                                </div>
                            </div>
                        </div>
                        <div className="div-table-cell" style={{ paddingRight: '10px', textAlign: 'center' }}>
                            {histItem.username}
                        </div>
                    </div>)}
                </div>
            </Modal>
        </ModalFrame>}

    </StyledFrame></PageFrame>;
}

export default CockpitDocumentsPage;
